/**
 * /security — change-password page, available to ANY authenticated role
 * (employee / owner / admin / hr).
 *
 * Spec called for mounting under `authedLayoutRoute`, but that layout's
 * beforeLoad bounces employees to `/me` — which would lock the role we
 * most want this page to serve. Mounted instead as a top-level route
 * under `rootRoute` with its own auth gate (no role check). The page
 * renders its own minimal chrome — header + sign-out — so it's
 * standalone from either sidebar context.
 */
import { Link, createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import {
  evaluatePasswordRules,
  validatePasswordStrength,
  type PasswordStrengthReason,
} from '@kora/types';
import { rootRoute } from './root.js';
import { isAuthenticated, useAuth } from '../auth/AuthContext.js';
import { changeMyPassword } from '../api/me.js';
import { ApiError } from '../api/client.js';

export const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security',
  beforeLoad: ({ location }) => {
    if (!isAuthenticated()) {
      throw redirect({
        to: '/login',
        search: { next: location.pathname },
      });
    }
  },
  component: SecurityPage,
});

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const FormSchema = z
  .object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z.string().min(1, 'Required'),
    confirmPassword: z.string().min(1, 'Required'),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'New password and confirmation must match.',
  });

function SecurityPage(): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<{
    field: 'currentPassword' | 'newPassword' | 'form';
    message: string;
  } | null>(null);
  const [successBanner, setSuccessBanner] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const newPassword = watch('newPassword');
  const confirmPassword = watch('confirmPassword');
  const currentPassword = watch('currentPassword');
  const rules = evaluatePasswordRules(newPassword);
  const allRulesPass = rules.every((r) => r.satisfied);
  const matches = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit =
    currentPassword.length > 0 && allRulesPass && matches && !isSubmitting;

  const mutation = useMutation({
    mutationFn: changeMyPassword,
    onSuccess: () => {
      setSuccessBanner(true);
      reset({ currentPassword: '', newPassword: '', confirmPassword: '' });
      // Short delay so the user sees the success message before navigating.
      setTimeout(() => {
        void navigate({ to: user?.role === 'employee' ? '/me' : '/dashboard' });
      }, 1800);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; reason?: PasswordStrengthReason } | null;
        if (err.status === 401 && body?.error === 'invalid_credentials') {
          setServerError({
            field: 'currentPassword',
            message: 'Current password is incorrect.',
          });
          return;
        }
        if (err.status === 400 && body?.error === 'weak_password') {
          setServerError({
            field: 'newPassword',
            message: weakPasswordLabel(body.reason),
          });
          return;
        }
        if (err.status === 400 && body?.error === 'same_password') {
          setServerError({
            field: 'newPassword',
            message: 'New password must be different from current password.',
          });
          return;
        }
        if (err.status === 429) {
          setServerError({
            field: 'form',
            message: 'Too many attempts. Wait a few minutes and try again.',
          });
          return;
        }
      }
      setServerError({
        field: 'form',
        message: 'Could not change your password. Try again.',
      });
    },
  });

  async function onSubmit(values: FormValues): Promise<void> {
    setServerError(null);
    setSuccessBanner(false);
    // Defensive: validate once more (client-side) so a stale strength
    // indicator can't sneak a weak password past the submit gate. The
    // server is still authoritative.
    const strength = validatePasswordStrength(values.newPassword);
    if (!strength.ok) {
      setServerError({
        field: 'newPassword',
        message: weakPasswordLabel(strength.reason),
      });
      return;
    }
    mutation.mutate({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
  }

  async function handleLogout(): Promise<void> {
    await logout();
    await navigate({ to: '/login' });
  }

  const homeTo = user?.role === 'employee' ? '/me' : '/dashboard';

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <header className="border-b border-gray-900 px-6 py-3 flex items-center justify-between">
        <Link to={homeTo} className="font-serif text-xl">
          LumoHR
        </Link>
        <div className="flex items-center gap-4 text-sm">
          {user ? (
            <span className="text-gray-400" title={user.email}>
              {user.email} · {user.role}
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleLogout}
            className="rounded border border-gray-700 px-3 py-1 text-xs hover:bg-gray-900"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 p-8">
        <section className="max-w-md mx-auto space-y-6">
          <div className="space-y-1">
            <Link
              to={homeTo}
              className="text-xs text-gray-500 hover:text-white underline-offset-2 hover:underline"
            >
              ← Back
            </Link>
            <h1 className="font-serif text-3xl">Security</h1>
          </div>

          <section className="rounded border border-gray-900 p-6 space-y-5">
            <header className="space-y-1">
              <h2 className="text-sm uppercase tracking-wide text-gray-400">
                Change password
              </h2>
              <p className="text-xs text-gray-500">
                Changing your password signs out every other device or
                browser you're signed in on.
              </p>
            </header>

            {successBanner ? (
              <div
                role="status"
                className="rounded border border-green-900 bg-green-950/40 px-4 py-3 text-sm text-green-300"
              >
                Password changed. Other devices have been signed out.
              </div>
            ) : null}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <Field
                id="currentPassword"
                label="Current password"
                type="password"
                autoComplete="current-password"
                error={
                  errors.currentPassword?.message ??
                  (serverError?.field === 'currentPassword'
                    ? serverError.message
                    : undefined)
                }
                inputProps={register('currentPassword')}
              />
              <Field
                id="newPassword"
                label="New password"
                type="password"
                autoComplete="new-password"
                error={
                  errors.newPassword?.message ??
                  (serverError?.field === 'newPassword'
                    ? serverError.message
                    : undefined)
                }
                inputProps={register('newPassword')}
              />
              <ul className="text-xs space-y-1">
                {rules.map((r) => (
                  <li
                    key={r.rule}
                    className={
                      r.satisfied ? 'text-green-400' : 'text-gray-500'
                    }
                  >
                    {r.satisfied ? '✓' : '○'} {r.label}
                  </li>
                ))}
                <li
                  className={
                    matches
                      ? 'text-green-400'
                      : confirmPassword.length > 0
                        ? 'text-red-400'
                        : 'text-gray-500'
                  }
                >
                  {matches ? '✓' : '○'} Confirmation matches new password
                </li>
              </ul>
              <Field
                id="confirmPassword"
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                error={errors.confirmPassword?.message}
                inputProps={register('confirmPassword')}
              />

              {serverError?.field === 'form' ? (
                <p role="alert" className="text-sm text-red-400">
                  {serverError.message}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full rounded bg-white text-black font-medium py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mutation.isPending ? 'Changing…' : 'Change password'}
              </button>
            </form>
          </section>

          <section className="rounded border border-gray-900 p-6 space-y-3">
            <header className="space-y-1">
              <h2 className="text-sm uppercase tracking-wide text-gray-400">
                Active sessions
              </h2>
              <p className="text-xs text-gray-500">
                See which devices are signed in to your account and
                sign out any you don&apos;t recognize.
              </p>
            </header>
            <Link
              to="/security/sessions"
              className="inline-block text-sm rounded border border-gray-700 px-4 py-2 hover:bg-gray-900"
            >
              Manage sessions →
            </Link>
          </section>
        </section>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function weakPasswordLabel(reason: PasswordStrengthReason | undefined): string {
  switch (reason) {
    case 'too_short':
      return 'Password must be at least 12 characters long.';
    case 'no_letter':
      return 'Password must contain at least one letter.';
    case 'no_number':
      return 'Password must contain at least one number.';
    default:
      return 'Password does not meet the strength requirements.';
  }
}

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  autoComplete?: string;
  error?: string;
  inputProps: ReturnType<ReturnType<typeof useForm<FormValues>>['register']>;
}

function Field({
  id,
  label,
  type = 'text',
  autoComplete,
  error,
  inputProps,
}: FieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label
        htmlFor={id}
        className="block text-xs uppercase tracking-wide text-gray-400"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
